import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Fuel, Pencil, Plus, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.bootstrap5.css';
import './SecurityPage.css';
import { cacheHasData, readCache, updateCacheIfChanged } from './utils/localCache';

const API_URL = 'https://jne-cilacap.com/slip/public/api';
const inputStyle = { width: '100%', padding: '11px 12px', boxSizing: 'border-box', border: '1.5px solid #dbe2ea', borderRadius: 11, outline: 'none', fontSize: 13, background: '#fff' };
const Field = ({ label, children }) => <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>{label}{children}</label>;

const localDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const getUser = () => {
  try { return JSON.parse(localStorage.getItem('data_user') || '{}'); } catch { return {}; }
};

const emptyForm = () => {
  const user = getUser();
  return {
    tanggal: localDate(),
    id_barang: '',
    driver: [user.firstname, user.lastname].filter(Boolean).join(' '),
    odo_awal: '',
    sisa_blok: '',
    nominal_bbm: '',
  };
};

const vehicleLabel = item => [item?.nama, item?.keterangan, item?.sn].filter(Boolean).join(' - ');
const formatNumber = value => value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString('id-ID');

const readDriverOptions = () => {
  try {
    const rows = JSON.parse(localStorage.getItem('security_options_driver') || '[]');
    return Array.isArray(rows) ? rows.filter(Boolean) : [];
  } catch { return []; }
};

const DriverSearchSelect = ({ value, onChange }) => {
  const selectRef = useRef(null);
  const instanceRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const saved = readDriverOptions();
    const options = value && !saved.some(item => item.toLowerCase() === value.toLowerCase()) ? [...saved, value] : saved;
    instanceRef.current = new TomSelect(selectRef.current, {
      options: options.map(item => ({ value: item, text: item })),
      valueField: 'value',
      labelField: 'text',
      searchField: ['text'],
      maxItems: 1,
      persist: false,
      createOnBlur: true,
      closeAfterSelect: true,
      selectOnTab: true,
      placeholder: 'Cari / tambah nama driver',
      create(input) {
        const clean = input.trim().slice(0, 20);
        return clean ? { value: clean, text: clean } : false;
      },
      onChange(nextValue) {
        const clean = String(nextValue || '').trim();
        if (clean) {
          const latest = readDriverOptions();
          if (!latest.some(item => item.toLowerCase() === clean.toLowerCase())) {
            localStorage.setItem('security_options_driver', JSON.stringify([...latest, clean]));
          }
        }
        onChangeRef.current(clean);
      },
    });
    instanceRef.current.setValue(value || '', true);
    return () => { instanceRef.current?.destroy(); instanceRef.current = null; };
  }, []);

  useEffect(() => {
    if (!instanceRef.current) return;
    if (value && !instanceRef.current.options[value]) instanceRef.current.addOption({ value, text: value });
    instanceRef.current.setValue(value || '', true);
  }, [value]);

  return <div className="security-tom-select"><select ref={selectRef} aria-label="Cari atau tambah nama driver" /></div>;
};

const MobilSearchSelect = ({ options, value, onChange }) => {
  const selectRef = useRef(null);
  const instanceRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!selectRef.current) return undefined;

    instanceRef.current = new TomSelect(selectRef.current, {
      options: options.map(item => ({
        value: String(item.id),
        text: vehicleLabel(item),
        nama: item.nama || '',
        keterangan: item.keterangan || '',
        sn: item.sn || '',
      })),
      valueField: 'value',
      labelField: 'text',
      searchField: ['nama', 'keterangan', 'sn', 'text'],
      maxItems: 1,
      closeAfterSelect: true,
      placeholder: '-- Cari dan Pilih Mobil --',
      render: {
        option: (item, escape) => `<div style="padding:8px 10px"><div style="font-weight:600;color:#1e293b">${escape(item.nama)}</div><div style="font-size:11px;color:#64748b;margin-top:3px">${escape([item.keterangan, item.sn].filter(Boolean).join(' - '))}</div></div>`,
        item: (item, escape) => `<div>${escape(item.text)}</div>`,
        no_results: () => '<div class="no-results">Mobil tidak ditemukan</div>',
      },
      onChange: nextValue => onChangeRef.current(nextValue || ''),
    });
    instanceRef.current.setValue(value || '', true);

    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [options]);

  useEffect(() => {
    instanceRef.current?.setValue(value || '', true);
  }, [value]);

  return <div className="security-tom-select"><select ref={selectRef} aria-label="Cari dan pilih mobil" /></div>;
};

export default function MonitoringBbmPage() {
  const navigate = useNavigate();
  const userId = getUser().id || 'guest';
  const mobilCacheKey = `monitoring_bbm_mobil_v1_${userId}`;
  const logCacheKey = `monitoring_bbm_today_v1_${userId}_${localDate()}`;
  const [logs, setLogs] = useState(() => readCache(logCacheKey, []));
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [mobil, setMobil] = useState(() => readCache(mobilCacheKey, []));
  const [loading, setLoading] = useState(() => !cacheHasData(mobilCacheKey) || !cacheHasData(logCacheKey));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editId, setEditId] = useState(null);

  const loadData = async () => {
    const user = getUser();
    if (!user.id) throw new Error('Data user login tidak ditemukan. Silakan login ulang.');

    const [mobilResponse, logResponse] = await Promise.all([
      fetch(`${API_URL}/barang/mobil-monitoring-bbm/${encodeURIComponent(user.id)}`),
      fetch(`${API_URL}/monitoring-bbm/${encodeURIComponent(user.id)}`),
    ]);
    const [mobilJson, logJson] = await Promise.all([mobilResponse.json(), logResponse.json()]);

    if (!mobilResponse.ok || !mobilJson.success) throw new Error(mobilJson.message || 'Daftar mobil gagal dimuat.');
    if (!logResponse.ok || !logJson.success) throw new Error(logJson.message || 'Riwayat BBM gagal dimuat.');
    const nextMobil = Array.isArray(mobilJson.data) ? mobilJson.data : [];
    const nextLogs = Array.isArray(logJson.data) ? logJson.data : [];
    if (updateCacheIfChanged(mobilCacheKey, nextMobil)) setMobil(nextMobil);
    if (updateCacheIfChanged(logCacheKey, nextLogs)) setLogs(nextLogs);
    setError('');
  };

  useEffect(() => {
    let active = true;
    const hasCache = cacheHasData(mobilCacheKey) && cacheHasData(logCacheKey);
    loadData().catch(err => { if (active && !hasCache) setError(err.message); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const filteredLogs = useMemo(() => logs.filter(item => {
    const text = [item.tanggal, item.driver, item.lokasi, vehicleLabel(item.barang)].join(' ').toLowerCase();
    return text.includes(search.toLowerCase());
  }), [logs, search]);

  const submit = async event => {
    event.preventDefault();
    if (!form.id_barang || !form.driver.trim() || form.odo_awal === '') {
      return Swal.fire('Data Belum Lengkap', 'Mobil, driver, dan odo awal wajib diisi.', 'warning');
    }

    setSaving(true);
    try {
      const user = getUser();
      const response = await fetch(editId ? `${API_URL}/monitoring-bbm/${editId}` : `${API_URL}/monitoring-bbm`, {
        method: editId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          tanggal: form.tanggal,
          id_barang: Number(form.id_barang),
          driver: form.driver.trim(),
          odo_awal: Number(form.odo_awal),
          sisa_blok: form.sisa_blok === '' ? null : Number(form.sisa_blok),
          nominal_bbm: form.nominal_bbm === '' ? null : Number(form.nominal_bbm),
        }),
      });
      const json = await response.json();
      if (!response.ok || !json.success) {
        const validationMessage = json.errors ? Object.values(json.errors).flat()[0] : null;
        throw new Error(validationMessage || json.message || 'Monitoring BBM gagal disimpan.');
      }

      await loadData();
      setForm(emptyForm());
      setEditId(null);
      setShowForm(false);
      Swal.fire({ icon: 'success', title: editId ? 'Monitoring BBM Diperbarui' : 'Monitoring BBM Tersimpan', text: json.data?.kemarin_diperbarui ? 'Odo akhir dan total jarak kemarin otomatis diperbarui.' : undefined, timer: 1800, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Gagal Menyimpan', err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = item => {
    setEditId(item.id);
    setForm({
      tanggal: String(item.tanggal).slice(0, 10),
      id_barang: String(item.id_barang),
      driver: item.driver || '',
      odo_awal: item.odo_awal ?? '',
      sisa_blok: item.sisa_blok ?? '',
      nominal_bbm: item.nominal_bbm ?? '',
    });
    setShowForm(true);
  };

  return <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(180deg,#F5F9FF,#fff)', display: 'flex', flexDirection: 'column', fontFamily: 'Poppins,sans-serif' }}>
    <header style={{ padding: 15, display: 'flex', alignItems: 'center', gap: 12 }}>
      <button onClick={() => navigate(-1)} style={{ width: 40, height: 40, border: 0, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,.15)', display: 'grid', placeItems: 'center' }}><ArrowLeft size={22} /></button>
      <h2 style={{ flex: 1, textAlign: 'center', margin: '0 40px 0 0', fontSize: 18, color: '#003087' }}>Monitoring BBM</h2>
    </header>
    <main style={{ flex: 1, overflowY: 'auto', padding: '15px 16px 80px' }}><div style={{ maxWidth: 620, margin: '0 auto' }}>
      <button onClick={openCreate} disabled={loading || !!error} style={{ width: '100%', padding: 15, border: 0, borderRadius: 16, background: '#f59e0b', color: '#fff', fontWeight: 800, display: 'flex', justifyContent: 'center', gap: 8, opacity: loading || error ? .6 : 1 }}><Plus size={18} />Catat Monitoring BBM</button>
      {error && <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#fef2f2', color: '#b91c1c', fontSize: 12 }}>{error}</div>}
      <section style={{ marginTop: 22, padding: 18, borderRadius: 22, background: '#fff', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 14px', color: '#003087', fontSize: 16 }}>Riwayat Hari Ini</h3>
        <div style={{ position: 'relative', marginBottom: 16 }}><Search size={17} style={{ position: 'absolute', left: 13, top: 13, color: '#94a3b8' }} /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari mobil, driver, atau lokasi" style={{ ...inputStyle, paddingLeft: 40 }} /></div>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Memuat data...</div> : filteredLogs.length ? filteredLogs.map(item => <article key={item.id} style={{ padding: '16px 15px', borderRadius: 18, background: 'linear-gradient(135deg,#fffbeb,#fff7d6)', border: '1px solid #fdecc8', marginBottom: 12, display: 'grid', gridTemplateColumns: '44px minmax(0,1fr) 40px', gap: 12, alignItems: 'start' }}>
          <div style={{ width: 44, height: 44, borderRadius: 13, background: '#fff', color: '#f59e0b', display: 'grid', placeItems: 'center', boxShadow: '0 3px 10px rgba(245,158,11,.12)' }}><Fuel size={22} /></div>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#172033', fontWeight: 800, fontSize: 15, lineHeight: 1.35, overflowWrap: 'anywhere' }}>{vehicleLabel(item.barang)}</div>
            <div style={{ marginTop: 5, color: '#64748b', fontSize: 12, lineHeight: 1.45 }}>{item.tanggal} <span style={{ color: '#cbd5e1' }}>·</span> {item.driver}</div>
            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              <span style={{ padding: '5px 9px', borderRadius: 9, background: 'rgba(255,255,255,.8)', color: '#475569', fontSize: 11, fontWeight: 700 }}>Odo awal {formatNumber(item.odo_awal)}</span>
              <span style={{ padding: '5px 9px', borderRadius: 9, background: 'rgba(255,255,255,.8)', color: '#475569', fontSize: 11, fontWeight: 700 }}>Sisa blok {formatNumber(item.sisa_blok)}</span>
              <span style={{ padding: '5px 9px', borderRadius: 9, background: '#fff3c4', color: '#9a5b00', fontSize: 11, fontWeight: 800 }}>BBM Rp {formatNumber(item.nominal_bbm)}</span>
            </div>
          </div>
          <button type="button" onClick={() => openEdit(item)} aria-label="Edit monitoring BBM" style={{ width: 40, height: 40, border: 0, borderRadius: 12, background: '#eff6ff', color: '#2563eb', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><Pencil size={17} /></button>
        </article>) : <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Belum ada data BBM.</div>}
      </section>
    </div></main>
    {showForm && <div onClick={() => !saving && setShowForm(false)} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,23,42,.48)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}><form onSubmit={submit} onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: '26px 26px 0 0', padding: '14px 20px 28px', display: 'flex', flexDirection: 'column', gap: 12, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><h3>{editId ? 'Edit Monitoring BBM' : 'Catat Monitoring BBM'}</h3><button type="button" disabled={saving} onClick={() => setShowForm(false)} style={{ border: 0, background: '#f1f5f9', width: 36, height: 36, borderRadius: 10 }}><X size={18} /></button></div>
      <Field label="Tanggal *"><input type="date" max={localDate()} disabled={!!editId} style={{ ...inputStyle, opacity: editId ? .65 : 1 }} value={form.tanggal} onChange={e => setForm(v => ({ ...v, tanggal: e.target.value }))} /></Field>
      <Field label="Pilih Mobil *"><div style={{ pointerEvents: editId ? 'none' : 'auto', opacity: editId ? .65 : 1 }}><MobilSearchSelect options={mobil} value={form.id_barang} onChange={id_barang => setForm(v => ({ ...v, id_barang }))} /></div></Field>
      <Field label="Driver *"><DriverSearchSelect value={form.driver} onChange={driver => setForm(v => ({ ...v, driver }))} /></Field>
      <Field label="Odo Awal *"><input type="number" min="0" style={inputStyle} value={form.odo_awal} onChange={e => setForm(v => ({ ...v, odo_awal: e.target.value }))} /></Field>
      <Field label="Sisa Blok"><input type="number" min="0" style={inputStyle} value={form.sisa_blok} onChange={e => setForm(v => ({ ...v, sisa_blok: e.target.value }))} /></Field>
      <Field label="Nominal BBM"><input type="number" min="0" style={inputStyle} value={form.nominal_bbm} onChange={e => setForm(v => ({ ...v, nominal_bbm: e.target.value }))} /></Field>
      <div style={{ padding: 11, borderRadius: 12, background: '#eff6ff', color: '#1e40af', fontSize: 12 }}>Odo awal ini otomatis menjadi odo akhir catatan kemarin untuk mobil yang sama. Total jarak kemarin dihitung otomatis.</div>
      <button type="submit" disabled={saving} style={{ padding: 14, border: 0, borderRadius: 14, background: '#f59e0b', color: '#fff', fontWeight: 800, opacity: saving ? .65 : 1 }}>{saving ? 'Menyimpan...' : editId ? 'Simpan Perubahan' : 'Submit'}</button>
    </form></div>}
  </div>;
}
